import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Briefcase,
  Calendar,
  ShieldCheck,
  MapPin,
  Mail,
  ChevronDown,
  ChevronUp,
  Plane,
  Users,
  Wrench,
  Scale,
  Repeat,
  CheckCircle2,
  Linkedin,
  Share2,
  Target,
  Flag,
} from 'lucide-react';
import JobAlertsForm from '../components/careers/JobAlertsForm';
import { WhatsAppCommunityBlock } from '../components/WhatsAppCommunity';

// =====================================================================
// /careers — Public job board landing.
// Free access. Read full ads inline. Apply directly to nicolas@gate33.co.
// =====================================================================

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 5;

// ---------- Helper: parse a description with emoji section headers ----------
// Sections separated by blank lines; first line of each section may start with
// an emoji + section title. The rest of the section uses "— " bullet lines.
const SECTION_ICON_MAP = {
  '✈️': Plane,
  '🔁': Repeat,
  '📍': MapPin,
  '🛠️': Wrench,
  '⚖️': Scale,
  '👥': Users,
  '🎯': CheckCircle2,
};

const parseSections = (raw) => {
  if (!raw) return [];
  const blocks = raw.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  return blocks.map((block) => {
    const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
    const firstLine = lines[0] || '';
    // Detect emoji + title pattern e.g. "✈️ Operations"
    const emojiMatch = firstLine.match(/^([\u2600-\u27BF\uD83C-\uDBFF\uDC00-\uDFFF\u2700-\u27FF\u2300-\u23FF\u2900-\u297F]+(?:\u200D[\u2600-\u27BF\uD83C-\uDBFF\uDC00-\uDFFF]+)*)\s+(.+)$/u);
    if (emojiMatch) {
      const emoji = emojiMatch[1];
      const title = emojiMatch[2];
      const bullets = lines.slice(1).map((l) => l.replace(/^[—–-]\s*/, ''));
      return { type: 'section', emoji, title, bullets };
    }
    // Plain paragraph block
    return { type: 'paragraph', text: lines.join(' ') };
  });
};

// ---------- WhatsApp glyph (lucide has no official one) ----------
const WhatsAppGlyph = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.52 3.48A11.94 11.94 0 0 0 12 0C5.37 0 .07 5.3.07 11.93c0 2.1.55 4.16 1.6 5.98L0 24l6.27-1.64a11.93 11.93 0 0 0 5.73 1.46h.01c6.62 0 11.92-5.3 11.92-11.93 0-3.19-1.24-6.18-3.41-8.41z" />
    <path d="M17.4 14.4c-.3-.15-1.77-.87-2.05-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.18.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.18-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.06 2.87 1.21 3.07.15.2 2.09 3.2 5.07 4.49.71.31 1.26.49 1.69.62.71.22 1.36.19 1.87.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z" />
  </svg>
);

// ---------- Share row — LinkedIn / WhatsApp / Copy link ----------
// We use buttons + window.open() (not <a href>) and neutral data-testids
// so social-tracker blockers do not strip these elements.
const ShareRow = ({ job }) => {
  const [copied, setCopied] = useState(false);
  const jobUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/careers#${job.id}`
    : `/careers#${job.id}`;
  const shareText = `${job.title} — via Gate 33 Careers`;

  const shareOn = (network) => {
    const urls = {
      li: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(jobUrl)}`,
      wa: `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${jobUrl}`)}`,
    };
    if (urls[network]) {
      window.open(urls[network], '_blank', 'noopener,noreferrer');
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(jobUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {}
  };

  const baseClass =
    'inline-flex items-center gap-2 px-4 py-2.5 border border-[#E5E0D8] text-xs uppercase tracking-[0.15em] font-semibold transition-all duration-300';

  return (
    <div
      data-testid={`job-share-row-${job.id}`}
      className="mt-8 pt-6 border-t border-[#E5E0D8] flex flex-wrap items-center gap-3"
    >
      <span className="overline text-[#9A9A9A] mr-2 inline-flex items-center gap-2">
        <Share2 size={13} /> Share
      </span>
      <button
        type="button"
        onClick={() => shareOn('li')}
        data-testid={`job-share-pro-${job.id}`}
        className={`${baseClass} text-[#1A1A1A] hover:border-[#C5A880] hover:text-[#C5A880]`}
        aria-label="Share on professional network"
      >
        <Linkedin size={14} strokeWidth={1.8} /> Professional
      </button>
      <button
        type="button"
        onClick={() => shareOn('wa')}
        data-testid={`job-share-msg-${job.id}`}
        className={`${baseClass} text-[#1A1A1A] hover:border-[#C5A880] hover:text-[#C5A880]`}
        aria-label="Share via message"
      >
        <WhatsAppGlyph size={14} /> Message
      </button>
      <button
        type="button"
        onClick={copyLink}
        data-testid={`job-share-copy-${job.id}`}
        className={`${baseClass} ${copied ? 'border-[#C5A880] text-[#C5A880]' : 'text-[#1A1A1A] hover:border-[#C5A880] hover:text-[#C5A880]'}`}
      >
        {copied ? 'Link copied ✓' : 'Copy link'}
      </button>
    </div>
  );
};

// ---------- Job Section — Pathway-style 2-column editorial layout ----------
const JobSection = ({ job, index }) => {
  const [open, setOpen] = useState(false);
  const sections = parseSections(job.description);
  const postedDate = job.posted_at ? job.posted_at.slice(0, 10) : null;
  const reverse = index % 2 === 1;

  // Extract a few highlight bullets to show in the visual side
  const firstSection = sections.find((s) => s.type === 'section');
  const highlights = firstSection?.bullets?.slice(0, 3) || [];

  return (
    <article
      id={job.id}
      data-testid={`job-section-${job.id}`}
      className="scroll-mt-24"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        {/* VISUAL SIDE — dark editorial block (with optional hero image) */}
        <div className={`${reverse ? 'lg:order-2' : ''}`}>
          <div className="relative aspect-[4/5] bg-[#0F0F0F] overflow-hidden">
            {job.image_url && (
              <>
                <img
                  src={job.image_url}
                  alt={`${job.title} — Gate 33 Careers`}
                  className="absolute inset-0 w-full h-full object-cover opacity-55"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/55 to-[#0F0F0F]/20" />
              </>
            )}
            <div className="relative z-10 h-full flex flex-col justify-between p-8 md:p-10 text-[#FDFBF7]">
              <div>
                <div className="flex items-center justify-between mb-10">
                  <span className="overline text-[#C5A880]">
                    {job.status === 'completed'
                      ? `Recruitment closed · ${String(index + 1).padStart(2, '0')}`
                      : `Now hiring · ${String(index + 1).padStart(2, '0')}`}
                  </span>
                  {job.status === 'completed' ? (
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A] border border-[#9A9A9A] px-2.5 py-1">
                      Recruitment closed
                    </span>
                  ) : job.verified ? (
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#C5A880] border border-[#C5A880] px-2.5 py-1">
                      <ShieldCheck size={10} strokeWidth={2} /> Verified
                    </span>
                  ) : null}
                </div>
                <div className="w-14 h-14 border border-[#C5A880] flex items-center justify-center mb-8">
                  <Plane size={22} className="text-[#C5A880]" strokeWidth={1.5} />
                </div>
                {job.reference && (
                  <span className="text-xs uppercase tracking-[0.18em] text-[#9A9A9A] block mb-3">
                    Ref. {job.reference}
                  </span>
                )}
                <h2 className="font-heading text-2xl md:text-3xl lg:text-[2rem] tracking-tight leading-[1.1] mb-4 text-[#FDFBF7]">
                  {job.title}
                </h2>
                <p className="text-sm text-[#D6D2CA] leading-relaxed">{job.operator}</p>
              </div>

              <div className="space-y-3 pt-6 border-t border-[#FDFBF7]/15 text-sm">
                <div className="flex items-start gap-3 text-[#D6D2CA]">
                  <MapPin size={14} className="text-[#C5A880] flex-shrink-0 mt-0.5" />
                  <span>{job.base}</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#C5A880] font-semibold">
                    {job.role_type || 'permanent'}
                  </span>
                  <span className="text-[#9A9A9A]">·</span>
                  {job.deadline ? (
                    <span className="text-[#D6D2CA] inline-flex items-center gap-1.5 text-xs">
                      <Calendar size={13} className="text-[#C5A880]" /> Closes {job.deadline}
                    </span>
                  ) : (
                    <span className="text-[#9A9A9A] inline-flex items-center gap-1.5 text-xs">
                      <Calendar size={13} /> Rolling applications
                    </span>
                  )}
                </div>
                {postedDate && (
                  <p className="text-xs text-[#9A9A9A]">Posted {postedDate}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* CONTENT SIDE */}
        <div className={`${reverse ? 'lg:order-1' : ''}`}>
          <span className="overline text-[#C5A880] inline-block mb-4">
            Position {String(index + 1).padStart(2, '0')}
          </span>
          <h3 className="font-heading text-2xl sm:text-3xl tracking-tight leading-tight mb-6 text-[#0F0F0F]">
            What this role really is.
          </h3>

          {/* Intro paragraph */}
          {sections[0] && sections[0].type === 'paragraph' && (
            <p className="text-[#1A1A1A] leading-relaxed mb-8">
              {sections[0].text}
            </p>
          )}

          {/* Quick highlights (3 first bullets of first section) — always visible */}
          {highlights.length > 0 && !open && (
            <div className="mb-8">
              <h4 className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1A1A1A] mb-4">
                Quick highlights
              </h4>
              <ul className="space-y-3">
                {highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3">
                    <CheckCircle2 size={16} className="text-[#C5A880] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                    <span className="text-[#1A1A1A] leading-relaxed text-sm">{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Full body when expanded */}
          {open && (
            <div className="space-y-7 mb-8">
              {sections.map((s, i) => {
                if (s.type === 'paragraph' && i === 0) return null;
                if (s.type === 'paragraph') {
                  return (
                    <p key={i} className="text-[#1A1A1A] leading-relaxed">
                      {s.text}
                    </p>
                  );
                }
                return (
                  <div key={i}>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-lg" aria-hidden="true">{s.emoji}</span>
                      <h4 className="font-heading text-base tracking-tight text-[#0F0F0F]">
                        {s.title}
                      </h4>
                    </div>
                    <ul className="space-y-2 pl-8">
                      {s.bullets.map((b, j) => (
                        <li key={j} className="text-[#1A1A1A] leading-relaxed text-sm flex items-baseline gap-2.5">
                          <span className="text-[#C5A880]">—</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}

              {/* Profile / Requirements */}
              {job.requirements && (
                <div className="pt-6 border-t border-[#E5E0D8]">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle2 size={16} className="text-[#C5A880]" strokeWidth={1.8} />
                    <h4 className="font-heading text-base tracking-tight text-[#0F0F0F]">Profile</h4>
                  </div>
                  <p className="text-[#1A1A1A] leading-relaxed text-sm whitespace-pre-line pl-8">
                    {job.requirements}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Expand / Collapse toggle */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid={`job-toggle-${job.id}`}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-semibold text-[#1A1A1A] hover:text-[#C5A880] transition-colors mb-8"
          >
            {open ? <>Collapse <ChevronUp size={14} /></> : <>Read the full ad <ChevronDown size={14} /></>}
          </button>

          {/* Apply CTA — always visible, premium block */}
          {job.status === 'completed' ? (
            <div
              data-testid={`job-completed-${job.id}`}
              className="bg-[#F5F1EA] border-l-2 border-[#9A9A9A] p-6 mb-6"
            >
              <div className="overline text-[#9A9A9A] mb-3">Recruitment closed</div>
              <p className="text-[#0F0F0F] leading-relaxed text-sm mb-5">
                Recruitment for this position is now closed. New operator postings are added regularly — subscribe to job alerts to be notified before the next one goes public.
              </p>
              <a
                href="#careers-job-alerts"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .querySelector('[data-testid="careers-job-alerts"]')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                data-testid={`job-notify-${job.id}`}
                className="inline-flex items-center gap-2 bg-[#0F0F0F] text-[#FDFBF7] px-7 py-3.5 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all duration-300"
              >
                <Mail size={14} /> Get notified about the next one
              </a>
            </div>
          ) : (
            job.how_to_apply && (
              <div className="bg-[#FDFBF7] border-l-2 border-[#C5A880] p-6 mb-6">
                <div className="overline text-[#C5A880] mb-3">Apply</div>
                <p className="text-[#0F0F0F] leading-relaxed text-sm whitespace-pre-line mb-5">
                  {job.how_to_apply}
                </p>
                <a
                  href={`mailto:nicolas@gate33.co?subject=${encodeURIComponent(
                    job.reference ? `${job.reference} – Your full name` : job.title
                  )}`}
                  data-testid={`job-apply-${job.id}`}
                  className="inline-flex items-center gap-2 bg-[#0F0F0F] text-[#FDFBF7] px-7 py-3.5 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all duration-300"
                >
                  <Mail size={14} /> Send application
                </a>
              </div>
            )
          )}

          {/* Share row */}
          <ShareRow job={job} />
        </div>
      </div>
    </article>
  );
};

// ---------- Pre-apply reminder — shown ONCE above the job list ----------
const PreApplyReminder = () => (
  <div
    data-testid="pre-apply-reminder"
    className="border border-[#C5A880] bg-white px-6 py-5 md:px-8 md:py-6 mb-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5"
  >
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 flex-shrink-0 border border-[#C5A880] flex items-center justify-center">
        <Flag size={16} className="text-[#C5A880]" strokeWidth={1.8} />
      </div>
      <div>
        <p className="font-heading text-lg text-[#0F0F0F] leading-tight mb-1">
          Not getting responses to your applications?
        </p>
        <p className="text-sm text-[#5A5A5A] leading-relaxed">
          Mentorship helps you reposition your profile and target the right operators.
        </p>
      </div>
    </div>
    <div className="flex flex-col sm:flex-row gap-2.5 md:gap-3 md:flex-shrink-0">
      <Link
        to="/mentorship"
        data-testid="pre-apply-mentorship-cta"
        className="inline-flex items-center justify-center gap-2 bg-[#0F0F0F] text-[#FDFBF7] px-5 py-2.5 uppercase tracking-[0.14em] text-[11px] font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all whitespace-nowrap"
      >
        Explore Mentorship
      </Link>
    </div>
  </div>
);

// ============================================================================
// PAGE
// ============================================================================
const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Gate 33 Careers — Private Aviation Job Board | Gate 33';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'A short, curated list of private aviation cabin attendant roles — gathered and verified by Gate 33. Apply directly to the operators.'
      );
    }
  }, []);

  const fetchJobs = useCallback(async (p) => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/api/careers/jobs`, {
        params: { page: p, page_size: PAGE_SIZE },
      });
      setJobs(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      setError('Unable to load the job board right now.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs(page);
  }, [page, fetchJobs]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="careers-page" className="min-h-screen">
      {/* HERO — image + dark overlay, editorial */}
      <section data-testid="careers-hero" className="relative bg-[#0F0F0F] min-h-[80vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src="/img/careers/hero-team.png"
            alt="Gate 33 — private aviation cabin crew and pilot in front of a business jet"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F0F]/40 via-[#0F0F0F]/55 to-[#0F0F0F]" />
        </div>
        <div className="relative z-10 container-custom max-w-5xl py-24 md:py-32">
          <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#C5A880] inline-block mb-6">
            Gate 33 Careers · Job Board
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-8 text-[#FDFBF7] max-w-4xl">
            Private aviation cabin attendant roles — curated by Gate 33.
          </h1>
          <p className="text-lg text-[#D6D2CA] leading-relaxed mb-6 max-w-3xl">
            A short list of operator and recruiter ads, gathered across the industry and shared here in full. Apply directly to the operators listed — Gate 33 does not act as an agency.
          </p>
          <p className="text-base text-[#C5A880] font-medium">
            Free access. No subscription. The list, kept short. Kept honest.
          </p>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section data-testid="careers-value" className="border-b border-[#E5E0D8] bg-white">
        <div className="container-custom grid grid-cols-1 md:grid-cols-3 gap-8 py-12">
          {[
            {
              icon: Briefcase,
              title: 'Curated by Gate 33',
              body: 'Operator postings, recruiter referrals, freelance missions — all checked before they go live here.',
            },
            {
              icon: ShieldCheck,
              title: 'Verified badge',
              body: 'When the badge is shown, Gate 33 knows the operator first-hand. An extra signal — not a filter.',
            },
            {
              icon: Calendar,
              title: 'Posted with deadlines',
              body: 'Each ad shows the publication date and when it closes. Expired ads disappear automatically.',
            },
          ].map((v, i) => (
            <div key={i} className="border-l-2 border-[#C5A880] pl-6">
              <v.icon size={20} className="text-[#C5A880] mb-4" strokeWidth={1.5} />
              <h3 className="font-heading text-lg text-[#0F0F0F] mb-2 leading-snug">{v.title}</h3>
              <p className="text-[#5A5A5A] text-sm leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* EDITORIAL — "Fascinating but not easy" — 2-column Pathway-style */}
      <section data-testid="careers-editorial" className="bg-[#FDFBF7] section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Content side */}
            <div>
              <span className="overline inline-block mb-6">Read this first</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] mb-10 text-[#0F0F0F]">
                Private aviation is a fascinating world — but fascinating doesn&rsquo;t mean easy.
              </h2>
              <div className="space-y-6 text-[#1A1A1A] text-base leading-relaxed">
                <p>
                  There are codes to decrypt before the right gates open. Most cabin crew who apply blindly to private aviation roles get filtered out in seconds — not because they lack potential, but because they apply without understanding the operator, the role, or how their profile reads to a business aviation recruiter.
                </p>
                <p className="text-[#0F0F0F] font-medium">
                  Before you apply to any of the positions below, take a moment. Ask yourself:
                </p>
                <ul className="space-y-3 border-l-2 border-[#C5A880] pl-6 text-[#1A1A1A]">
                  <li>Does my CV speak the language of private aviation, or still that of commercial?</li>
                  <li>Do I understand what this specific operator is really looking for?</li>
                  <li>Do I match the actual requirements, or am I applying on hope?</li>
                </ul>
                <p className="text-[#5A5A5A] italic">
                  If the answer to any of these is &ldquo;not yet,&rdquo; applying right now is the fastest way to be rejected. Gate 33 was built to help you change that — before you waste your shot.
                </p>
              </div>
            </div>

            {/* Visual side — image with editorial overlay */}
            <div className="lg:sticky lg:top-28">
              <div className="relative aspect-[4/5] bg-[#0F0F0F] overflow-hidden">
                <img
                  src="/img/careers/editorial-crew.png"
                  alt="Gate 33 — two corporate cabin attendants in private jet cabin"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#C5A880] font-semibold mb-3">
                    The reality
                  </p>
                  <p className="font-heading text-2xl md:text-3xl tracking-tight text-[#FDFBF7] leading-tight">
                    Most applications fail in the first 8 seconds — before the operator even finishes reading the CV.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONVERSION — Mentorship for active job seekers */}
      <section data-testid="careers-conversion" className="bg-white border-y border-[#E5E0D8] section-padding">
        <div className="container-custom max-w-5xl">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="overline inline-block mb-5">For active job seekers</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] text-[#0F0F0F] mb-6">
              Applications going unanswered?
            </h2>
            <p className="text-[#5A5A5A] leading-relaxed">
              Mentorship helps you reposition your profile and target the right operators — so the next application actually gets a reply.
            </p>
          </div>

          <article
            data-testid="careers-conversion-mentorship-card"
            className="border border-[#0F0F0F] bg-[#0F0F0F] text-[#FDFBF7] p-10 md:p-14 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 items-center"
          >
            <div>
              <div className="flex items-center gap-3 mb-5">
                <Target size={22} className="text-[#C5A880]" strokeWidth={1.5} />
                <span className="text-xs uppercase tracking-[0.2em] text-[#C5A880] font-semibold">Gate 33 Mentorship</span>
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl tracking-tight leading-tight text-[#FDFBF7] mb-4">
                Get positioned. Get hired by the right operator.
              </h3>
              <ul className="space-y-2.5 mb-2">
                {[
                  'Full CV & LinkedIn rebuild',
                  'Targeted operator shortlist matched to your profile',
                  '1:1 calls with Nicolas — no group coaching',
                  'Mock interviews & continuous WhatsApp support',
                ].map((it) => (
                  <li key={it} className="flex items-start gap-3 text-sm text-[#D6D2CA]">
                    <CheckCircle2 size={14} className="text-[#C5A880] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col gap-3 lg:min-w-[220px]">
              <Link
                to="/mentorship"
                data-testid="careers-conversion-mentorship-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-8 py-3.5 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
              >
                Explore Mentorship <ArrowRight size={14} />
              </Link>
              <a
                href="https://calendly.com/nicolas-gate33/discovery-call"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="careers-conversion-discovery-cta"
                className="inline-flex items-center justify-center gap-2 border border-[#C5A880] text-[#C5A880] px-8 py-3.5 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all duration-300"
              >
                Book a Discovery Call
              </a>
            </div>
          </article>

          <p className="text-center text-sm text-[#5A5A5A] mt-12">
            Just starting out? Explore the{' '}
            <Link to="/academy" data-testid="conversion-academy-link" className="text-[#1A1A1A] underline underline-offset-4 decoration-[#C5A880] hover:text-[#C5A880] transition-colors">
              3-Day Training in Nice
            </Link>{' '}
            — our flagship program for cabin crew transitioning into private aviation.
          </p>
        </div>
      </section>

      {/* JOB BOARD */}
      <section
        data-testid="careers-board"
        className="bg-[#FDFBF7] section-padding"
      >
        <div className="container-custom max-w-7xl">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-10">
            <div>
              <span className="overline inline-block mb-2">The list</span>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight">
                Currently open positions
              </h2>
            </div>
            <span className="text-sm text-[#5A5A5A]">
              {loading ? 'Loading…' : `${total} active ad${total === 1 ? '' : 's'}`}
            </span>
          </div>

          {loading && (
            <div className="py-12 text-center overline text-[#9A9A9A]">Loading the board…</div>
          )}

          {!loading && error && (
            <div className="py-16 text-center border border-dashed border-[#E5E0D8] bg-white">
              <p className="overline text-[#C0392B] mb-3">Something went wrong</p>
              <p className="text-[#5A5A5A] max-w-md mx-auto">
                {error} Please refresh the page in a moment.
              </p>
            </div>
          )}

          {!loading && !error && jobs.length === 0 && (
            <div
              data-testid="careers-empty"
              className="py-16 px-6 text-center border border-dashed border-[#E5E0D8] bg-white"
            >
              <p className="overline text-[#C5A880] mb-4">No active openings right now</p>
              <p className="text-[#1A1A1A] max-w-lg mx-auto mb-8 leading-relaxed">
                New positions are added regularly as credible operators open roles. Subscribe below to be notified first — before the next one goes public.
              </p>
              <a
                href="#careers-job-alerts"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('[data-testid="careers-job-alerts"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                data-testid="careers-empty-cta"
                className="inline-flex items-center gap-2 bg-[#0F0F0F] text-[#FDFBF7] px-8 py-3.5 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all duration-300"
              >
                Get notified <ArrowRight size={14} />
              </a>
            </div>
          )}

          {!loading && !error && jobs.length > 0 && (
            <>
              <PreApplyReminder />
              <div className="space-y-24 md:space-y-32">
                {jobs.map((j, i) => (
                  <JobSection key={j.id} job={j} index={i} />
                ))}
              </div>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              className="flex items-center justify-center gap-3 mt-12"
              data-testid="careers-pagination"
            >
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="text-xs uppercase tracking-[0.15em] px-4 py-2 border border-[#E5E0D8] hover:border-[#C5A880] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ← Previous
              </button>
              <span className="overline text-[#5A5A5A]">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="text-xs uppercase tracking-[0.15em] px-4 py-2 border border-[#E5E0D8] hover:border-[#C5A880] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 transition-colors"
              >
                Next <ArrowRight size={12} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* JOB ALERTS — opt-in */}
      <section
        data-testid="careers-job-alerts"
        className="bg-white border-y border-[#E5E0D8]"
      >
        <div className="container-custom max-w-5xl section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-2">
              <span className="overline inline-block mb-5">Direct to your inbox</span>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-[1.1] mb-6 text-[#0F0F0F]">
                The next opening, before it&rsquo;s public.
              </h2>
              <p className="text-[#5A5A5A] leading-relaxed mb-5">
                When a credible operator opens a position, the listing goes here — and to a short list of subscribers, first.
              </p>
              <p className="text-[#5A5A5A] leading-relaxed">
                One curated email. Real openings. No headhunter noise, no auto-blasts, no &ldquo;exclusive opportunities&rdquo; that turn out to be airline jobs.
              </p>
              <div className="mt-8 pt-6 border-t border-[#E5E0D8] space-y-3 text-sm text-[#1A1A1A]">
                <div className="flex items-start gap-3">
                  <span className="text-[#C5A880] mt-0.5">—</span>
                  <span>Sent only when a real operator role opens</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#C5A880] mt-0.5">—</span>
                  <span>Roughly 1–2 emails per month, never more</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#C5A880] mt-0.5">—</span>
                  <span>Unsubscribe in one click, anytime</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-3">
              <JobAlertsForm />
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Community — keep talents close to the operator pulse */}
      <WhatsAppCommunityBlock
        eyebrow="Stay close"
        title="Join the Gate 33 community on WhatsApp."
        body="A quiet feed where private aviation crew exchange operator news, fresh openings, and the small details that make a transition succeed. No noise."
      />

      {/* FINAL — CV advice */}
      <section className="section-dark py-16 md:py-20 border-b border-[#C5A880]/40">
        <div className="container-custom text-center max-w-3xl">
          <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#C5A880] inline-block mb-5">
            One last thing
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-6 text-[#FDFBF7]">
            Make your CV land like a private cabin attendant&rsquo;s.
          </h2>
          <p className="text-[#9A9A9A] max-w-xl mx-auto mb-10">
            Operators do not read airline CVs the way airline HR does. A one-page, operator-grade profile signals autonomy, mobility, and service intelligence — and gets opened first.
          </p>
          <Link
            to="/mentorship"
            data-testid="careers-cv-cta"
            className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
          >
            Upgrade your profile <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
